import { Check, Copy } from 'lucide-react';
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { Button } from '../Button';

/**
 * The card's `KEY-NNNN` reference beside the card back's title, with a Copy
 * button — `docs/notes/card-keys.md`, "Web". The reference is what people paste
 * into Slack, so the button copies the PLAIN reference (`MG-0001`), never a URL;
 * the share panel's "Copy link" owns the URL.
 *
 * ⚠ **The caller renders this only when the card's `ref` is non-null.** The
 * server scopes `ref` per viewer: `null` for an inbox card (no board, no number)
 * and for a viewer outside the reference scope (a public-arm or anonymous
 * reader), for whom a pasted reference would 404. Showing them a reference to
 * copy would hand them an address that does not open.
 *
 * ⚠ The reference is DATA and never a catalogue entry; only the button's words
 * and the announcement are routed.
 *
 * States: idle → `Copy`; copied → `Copied` for {@link COPIED_MS}, announced
 * once through the `role="status"` region; failed (no clipboard, or the write
 * was refused) → a sentence telling the reader to select the reference, which
 * is plain selectable text for exactly that reason.
 */
export const COPIED_MS = 2_000;

type CopyState = 'idle' | 'copied' | 'failed';

export interface CardReferenceCopyProps {
  /** `MG-0001` — the card's `ref`, as the server scoped it to this viewer. */
  readonly reference: string;
}

export function CardReferenceCopy({ reference }: CardReferenceCopyProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const [state, setState] = useState<CopyState>('idle');
  const resetTimer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(resetTimer.current), []);

  async function copy(): Promise<void> {
    window.clearTimeout(resetTimer.current);
    const clipboard = typeof navigator === 'undefined' ? undefined : navigator.clipboard;
    try {
      if (clipboard?.writeText === undefined) throw new Error('no clipboard');
      await clipboard.writeText(reference);
      setState('copied');
      resetTimer.current = window.setTimeout(() => setState('idle'), COPIED_MS);
    } catch {
      setState('failed');
    }
  }

  const copied = state === 'copied';

  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <span className="text-sm font-semibold tabular-nums tracking-wide text-ink-muted">
        {reference}
      </span>
      <Button
        type="button"
        size="sm"
        variant="quiet"
        aria-label={
          copied
            ? t('cardBack.reference.copiedLabel', { reference })
            : t('cardBack.reference.copyLabel', { reference })
        }
        iconStart={
          copied ? (
            <Check aria-hidden="true" className="size-3.5" />
          ) : (
            <Copy aria-hidden="true" className="size-3.5" />
          )
        }
        onClick={() => void copy()}
      >
        {copied ? t('cardBack.reference.copied') : t('cardBack.reference.copy')}
      </Button>
      <span role="status" className="sr-only">
        {copied ? t('cardBack.reference.announceCopied', { reference }) : ''}
      </span>
      {state === 'failed' ? (
        <span className="text-xs text-ink-muted">
          {t('cardBack.reference.copyFailed')}
        </span>
      ) : null}
    </span>
  );
}
